// src/pages/ManuscriptStructure.jsx
//
// The Manuscript Structure page: the front, body and back matter of a book, one
// element at a time, with a live preview of the page each one produces.
//
// Two entry points, one page. `/manuscript/<id>/structure` from the ⋮ menu on a
// draft card in the Manuscripts view, and `/bookcover/<id>/structure` from the ⋮
// menu on a cover card in My Covers. A standalone cover is a draft like any
// other, so the difference is the draft's `type` rather than the route: when it
// is `cover_only` the Manuscript Editor half of the page is hidden — its mode
// tab, its tool link and its elements — and the page is titled for the cover.
//
// Three columns. The element list on the left, the template editor in the middle,
// the preview on the right. Two of the twenty-six elements — the title page and
// the colophon — belong to Designer's Pages rather than the Manuscript Editor,
// which is what the mode toggle above the columns switches between.
//
// **Persistence.** Element state is read off `GET /api/drafts/<id>` (it rides
// along on the draft) and written back with `PUT /api/drafts/<id>/structure`,
// debounced. The catalogue in `src/config/manuscriptElements.js` supplies the
// defaults, and saved state is overlaid on top of them — so an element added to
// the catalogue later shows up with its defaults rather than as a hole.
//
// Turning an element on puts it in the book: `backend/utils/manuscript_pdf.py`
// composes the switched-on front and back matter around the manuscript body when
// the PDF is exported and when the book is published. Elements start off, because
// a book has the sections its author chose rather than all twenty-six.
//
// Colours come from `structureTokens.js`, which maps the prototype's palette onto
// the app's primary and secondary. The page keeps the app's global <Navbar />
// by staying out of `OWN_NAV_ROUTES` in App.js.
//
// The preview column deliberately does not reuse BookPreviewModal; see the note in
// StructurePreview.jsx for why.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { API_BASE } from '../utils/api';
import {
  CATEGORIES,
  LOCATIONS,
  MANUSCRIPT_ELEMENTS,
  defaultStructureState,
  defaultElementState,
  defaultFields,
  defaultFormat,
  defaultPosition,
  elementsForCategory,
  elementsForLocation,
} from '../config/manuscriptElements';
import {
  SP, FONT, CATEGORY_COLOR, eyebrowSx, BP, FLOATING_PANEL_W,
} from '../components/manuscript/structureTokens';
import ElementToggle from '../components/manuscript/ElementToggle';
import StructureFormatToolbar from '../components/manuscript/StructureFormatToolbar';
import TemplateFields from '../components/manuscript/TemplateFields';
import ConfirmModal from '../components/editor/ConfirmModal';
import { DEFAULT_BOOK_SIZE } from '../config/bookSizes';
import StructurePreview from '../components/manuscript/StructurePreview';
import {
  backMatterContents,
  coverDesignerPath,
  manuscriptContents,
  mergeStructure,
  nextEnabledElementId,
} from '../utils/manuscriptStructure';

// How long to wait after the last change before saving. Long enough that typing a
// sentence is one request, short enough that leaving the page loses nothing.
const SAVE_DEBOUNCE_MS = 800;

const POSITIONS = ['top', 'center', 'bottom'];

export default function ManuscriptStructure() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [draft, setDraft] = useState(null);
  const [structure, setStructure] = useState(() => defaultStructureState());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error

  // ?element=<id> — how the editor's Elements panel lands on the page the
  // author clicked rather than on the default. All three pieces of "where am
  // I" come from it together: the mode and category tabs frame the list the
  // current element has to be visible in, so setting the id alone would open
  // a back-matter page with the Front Matter tab lit.
  //
  // Recomputed per draft, not once per mount, and not on every render. The
  // author navigates within the page constantly and re-reading on each render
  // would drag them back to where they came in; but the page also stays
  // mounted across a change of draft — the cross-link from a linked cover to
  // its manuscript is this same route with a different id — and reading once
  // left that second draft opening wherever the first one had been left.
  const landing = useMemo(() => {
    const wanted = new URLSearchParams(window.location.search).get('element');
    return MANUSCRIPT_ELEMENTS.find((el) => el.id === wanted) || null;
  }, [id]);

  const [activeMode, setActiveMode] = useState(landing?.loc || 'editor');
  const [activeCategory, setActiveCategory] = useState(
    landing && landing.loc === 'editor' ? landing.category : 'front',
  );
  const [currentId, setCurrentId] = useState(landing?.id || 'copyright-page');
  const [resetOpen, setResetOpen] = useState(false);
  // Only meaningful at 900px and below, where the preview floats over the page.
  // Above that it is the grid's third column and this is ignored — the same
  // shape as the Cork Board's `panelOpen` and the editor's `railOpen`.
  const [previewOpen, setPreviewOpen] = useState(false);

  const accessToken = localStorage.getItem('rv_access_token');

  // Nothing is saved until the draft has been loaded and the author has actually
  // changed something — without this the first render would immediately write the
  // defaults back over whatever was stored.
  const dirtyRef = useRef(false);

  // --- load ---------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError('');
      try {
        const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
        const res = await fetch(`${API_BASE}/api/drafts/${id}`, {
          credentials: 'omit',
          headers,
        });
        if (!res.ok) {
          throw new Error(res.status === 403
            ? 'You do not have access to this manuscript.'
            : 'That manuscript could not be loaded.');
        }
        const data = await res.json();
        if (cancelled) return;
        setDraft(data);
        setStructure(mergeStructure(data.manuscriptStructure));

        // Back to where this URL says to open, before the override below reads
        // it. On a first mount this restates the initial state and costs
        // nothing; on a change of draft it is the whole fix. The component is
        // not remounted when only the id changes, so without this a manuscript
        // reached from a linked cover opened on the cover half, while the very
        // same URL loaded cold opened on Copyright Page.
        setActiveMode(landing?.loc || 'editor');
        setActiveCategory(landing && landing.loc === 'editor' ? landing.category : 'front');
        setCurrentId(landing?.id || 'copyright-page');

        // A standalone cover has no manuscript side, so the page opens on the
        // Designer's Pages half and stays there. The element it would otherwise
        // have landed on — `copyright-page` by default, or whatever ?element=
        // asked for — may belong to the half that is now hidden, which would
        // leave the editor column showing a page the list cannot select.
        if (data.type === 'cover_only') {
          setActiveMode('cover');
          setCurrentId((prev) => {
            const opened = MANUSCRIPT_ELEMENTS.find((el) => el.id === prev);
            if (opened && opened.loc === 'cover') return prev;
            return elementsForLocation('cover')[0]?.id || prev;
          });
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'That manuscript could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [id, accessToken, landing]);

  // --- save ---------------------------------------------------------------

  useEffect(() => {
    if (!dirtyRef.current) return undefined;

    setSaveState('saving');
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/drafts/${id}/structure`, {
          method: 'PUT',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ elements: structure }),
        });
        setSaveState(res.ok ? 'saved' : 'error');
      } catch {
        setSaveState('error');
      }
    }, SAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [structure, id, accessToken]);

  /** Change one element's state. Every edit on this page goes through here. */
  const updateElement = useCallback((elementId, patch) => {
    dirtyRef.current = true;
    setStructure((prev) => ({
      ...prev,
      [elementId]: { ...prev[elementId], ...patch },
    }));
  }, []);

  /** Change one field's value on one element. */
  const updateField = useCallback((elementId, fieldKey, value) => {
    dirtyRef.current = true;
    setStructure((prev) => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        fields: { ...prev[elementId].fields, [fieldKey]: value },
      },
    }));
  }, []);

  /**
   * Put one element back to its catalogue defaults — fields, position and
   * formatting. `enabled` is deliberately left alone: whether an element is in
   * the book is a decision about the book, not part of the template, and losing
   * it to a button labelled "reset this template" would be a surprise.
   */
  const resetElement = useCallback((element) => {
    dirtyRef.current = true;
    setStructure((prev) => ({
      ...prev,
      [element.id]: {
        ...prev[element.id],
        position: defaultPosition(element),
        format: defaultFormat(element),
        fields: defaultFields(element),
      },
    }));
  }, []);

  // --- derived ------------------------------------------------------------

  const modeElements = useMemo(() => elementsForLocation(activeMode), [activeMode]);
  const listedElements = useMemo(
    () => (activeMode === 'editor' ? elementsForCategory(activeCategory) : elementsForLocation('cover')),
    [activeMode, activeCategory],
  );
  const current = MANUSCRIPT_ELEMENTS.find((el) => el.id === currentId) || MANUSCRIPT_ELEMENTS[0];
  const currentState = structure[current.id] || defaultElementState(current);

  // The Table of Contents lists what the book will print — the manuscript's
  // chapters, then the back matter switched on — not rows typed here. Chapters are
  // read off the stored manuscript once per load; the back matter follows the
  // switches as they flip. The preview gets the same list in place of the typed
  // rows, which stay stored and untouched.
  const manuscriptChapters = useMemo(() => manuscriptContents(draft?.content), [draft?.content]);
  const tocContents = useMemo(
    () => (current.id === 'toc'
      ? { chapters: manuscriptChapters, backMatter: backMatterContents(structure) }
      : null),
    [current.id, manuscriptChapters, structure],
  );
  const previewState = useMemo(
    () => (tocContents
      ? {
        ...currentState,
        fields: { ...currentState.fields, rows: [...tocContents.chapters, ...tocContents.backMatter] },
      }
      : currentState),
    [tocContents, currentState],
  );

  const coverPath = coverDesignerPath(draft);
  const hasCover = Boolean(coverPath);

  // Which of the two shapes this page is in. `type` is the same discriminator
  // the Library sorts its cards by, so a draft cannot be a standalone cover in
  // one place and a manuscript in another.
  const coverOnly = draft?.type === 'cover_only';

  const enabledInMode = modeElements.filter((el) => structure[el.id]?.enabled);
  const noneEnabled = MANUSCRIPT_ELEMENTS.every((el) => !structure[el.id]?.enabled);
  const positionInMode = enabledInMode.findIndex((el) => el.id === currentId) + 1;

  /**
   * How many of a set of elements the author has actually put in the book.
   *
   * Both tab rows count this way. The number on a tab answers "how many have I
   * included?" rather than "how many exist" — the catalogue total is the same
   * for every author and says nothing about their own book.
   *
   * Deliberately not memoized. It has to move the instant the include switch
   * flips, and a useMemo here is one stale dependency away from a number that
   * only updates on remount. The work is a filter over twenty-six entries.
   */
  const countEnabled = (elements) => elements.filter((el) => structure[el.id]?.enabled).length;

  /** Step to the next or previous page of the book. */
  const stepPage = useCallback((delta) => {
    const targetId = nextEnabledElementId(
      elementsForLocation(activeMode), structure, currentId, delta,
    );
    if (!targetId) return;
    const target = MANUSCRIPT_ELEMENTS.find((el) => el.id === targetId);
    setCurrentId(targetId);
    if (target.loc === 'editor') setActiveCategory(target.category);
  }, [activeMode, structure, currentId]);

  /** Select an element, following it into its mode and category. */
  const goToElement = useCallback((elementId) => {
    const element = MANUSCRIPT_ELEMENTS.find((el) => el.id === elementId);
    if (!element) return;
    setActiveMode(element.loc);
    if (element.loc === 'editor') setActiveCategory(element.category);
    setCurrentId(elementId);
  }, []);

  // --- render -------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', py: 10, px: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>{loadError}</Typography>
        <Button onClick={() => navigate('/library')} startIcon={<ArrowBackIcon />}>
          Back to Manuscripts
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: SP.lavender, minHeight: '100%' }}>

      {/* ── page title row ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          px: 3.5,
          py: 2,
          backgroundColor: SP.purpleDeep,
          borderBottom: `1px solid ${SP.lineLt}`,
        }}
      >
        <Box>
          <Typography
            component="h1"
            sx={{ fontFamily: FONT.serif, fontSize: 19, fontWeight: 600, color: SP.white }}
          >
            {coverOnly ? 'Book Cover Structure Template' : 'Manuscript Structure Templates'}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.55)', mt: '2px' }}>
            {draft?.title ? `${draft.title} — ` : ''}
            {coverOnly
              ? 'Pick an element, fill in its template, watch the cover update.'
              : 'Pick an element, fill in its template, watch the book update.'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <SaveIndicator state={saveState} />
          {/* A standalone cover has no manuscript body, so /editor/<id> would
              open an empty document rather than the thing the author came from. */}
          {!coverOnly && (
            <ToolLink to={`/editor/${id}`} dotColor={SP.purpleSoft}>
              Manuscript Editor
            </ToolLink>
          )}

          {/* A standalone cover linked to a manuscript sends the author across
              rather than growing a second Manuscript Editor tab of its own.
              Structure is stored on the draft it was typed into, so front matter
              edited here would save onto the cover's record and never reach the
              book — two dedications for one title, with nothing to say which one
              prints. The link goes where that data actually belongs. */}
          {coverOnly && draft?.linkedManuscriptId && (
            <ToolLink
              to={`/manuscript/${draft.linkedManuscriptId}/structure`}
              dotColor={SP.purpleSoft}
            >
              Manuscript Structure
            </ToolLink>
          )}
          <ToolLink
            to={coverPath}
            dotColor={SP.goldLight}
            disabled={!hasCover}
            disabledTitle={coverOnly
              ? 'This cover has no design yet. Open it from its card in My Covers to start one.'
              : 'This manuscript has no cover yet. Link or create one first.'}
          >
            Cover Designer
          </ToolLink>
        </Box>
      </Box>

      {/* ── mode toggle ──
          Hidden for a standalone cover. With the Manuscript Editor half gone
          there is nothing left to toggle, and a lone tab that cannot be
          switched away from reads as a dead control rather than a choice. */}
      {!coverOnly && (
        <Box sx={{
          display: 'flex',
          gap: 1.25,
          px: 3.5,
          pt: 2,
          backgroundColor: SP.white,
          // The labels are monospace and will not wrap, so side by side the pair
          // has a hard floor of about 350px and runs off a phone. Stacked they
          // read the same and fit.
          [BP.phone]: { flexDirection: 'column', px: 2 },
        }}>
          {Object.values(LOCATIONS).map((loc) => {
            const count = countEnabled(elementsForLocation(loc.id));
            const active = activeMode === loc.id;
            return (
              <Box
                key={loc.id}
                component="button"
                type="button"
                onClick={() => {
                  setActiveMode(loc.id);
                  const first = elementsForLocation(loc.id)[0];
                  if (first) {
                    setCurrentId(first.id);
                    if (loc.id === 'editor') setActiveCategory(first.category);
                  }
                }}
                sx={{
                  flex: 1,
                  maxWidth: 280,
                  // Without this the button cannot shrink below its own text,
                  // which is what pushed it off the side of the screen.
                  minWidth: 0,
                  [BP.phone]: { maxWidth: 'none' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 2,
                  py: 1.5,
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontFamily: FONT.mono,
                  fontSize: 12,
                  letterSpacing: '.03em',
                  transition: 'all .2s ease',
                  border: `1.5px solid ${active ? SP.purple : SP.lineSoft}`,
                  backgroundColor: active ? SP.lavender : SP.white,
                  color: active ? SP.purple : SP.gray,
                  fontWeight: active ? 600 : 400,
                  '&:hover': { borderColor: SP.purpleSoft },
                }}
              >
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: loc.id === 'editor' ? SP.purpleSoft : SP.goldLight,
                }} />
                {loc.label}
                <Box component="span" sx={{ ml: 'auto', fontSize: 10.5, opacity: 0.65 }}>
                  {count} element{count === 1 ? '' : 's'}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── category tabs (Manuscript Editor only) ── */}
      {activeMode === 'editor' && (
        <Box sx={{
          display: 'flex',
          gap: 1.25,
          flexWrap: 'wrap',
          px: 3.5,
          py: 1.75,
          backgroundColor: SP.white,
          borderBottom: `1px solid ${SP.lineSoft}`,
        }}>
          {Object.values(CATEGORIES).map((cat) => {
            const count = countEnabled(elementsForCategory(cat.id));
            const active = activeCategory === cat.id;
            return (
              <Box
                key={cat.id}
                component="button"
                type="button"
                onClick={() => {
                  setActiveCategory(cat.id);
                  const first = elementsForCategory(cat.id)[0];
                  if (first) setCurrentId(first.id);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2.25,
                  py: 1.15,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontFamily: FONT.mono,
                  fontSize: 11.5,
                  letterSpacing: '.04em',
                  textTransform: 'uppercase',
                  transition: 'all .18s ease',
                  border: `1px solid ${active ? SP.gold : SP.lineSoft}`,
                  backgroundColor: active ? SP.gold : SP.lavender,
                  color: active ? SP.purpleDeep : SP.purple,
                  fontWeight: active ? 600 : 400,
                  '&:hover': { borderColor: active ? SP.gold : SP.purpleSoft },
                }}
              >
                {cat.label}
                <Box component="span" sx={{ fontSize: 10, opacity: active ? 0.7 : 0.6 }}>{count}</Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── position strip ── */}
      <Box sx={{ px: 3.5, pt: 1.25, backgroundColor: SP.white }}>
        <Box sx={{
          display: 'flex',
          height: 8,
          borderRadius: '4px',
          overflow: 'hidden',
          border: `1px solid ${SP.lineSoft}`,
        }}>
          {modeElements.map((el) => {
            const isCurrent = el.id === currentId;
            const isOff = !structure[el.id]?.enabled;
            return (
              <Box
                key={el.id}
                component="button"
                type="button"
                title={`${el.name}${isOff ? ' (hidden)' : ''}`}
                aria-label={`${el.name}${isOff ? ', hidden' : ''}`}
                onClick={() => goToElement(el.id)}
                sx={{
                  flex: 1,
                  p: 0,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'opacity .15s ease',
                  opacity: isOff ? 0.12 : (isCurrent ? 1 : 0.35),
                  backgroundColor: CATEGORY_COLOR[el.category],
                  ...(isOff && {
                    background: `repeating-linear-gradient(45deg, ${SP.gray} 0 3px, transparent 3px 6px)`,
                  }),
                  '&:hover': { opacity: isOff ? 0.3 : 0.8 },
                }}
              />
            );
          })}
        </Box>
        <Typography sx={{
          fontFamily: FONT.mono,
          fontSize: 10,
          color: SP.gray,
          mt: 0.75,
          pb: 1.75,
          textAlign: 'center',
        }}>
          {positionInMode > 0
            ? `Element ${positionInMode} of ${enabledInMode.length} visible`
            : 'Hidden element'}
          {' in '}{LOCATIONS[activeMode].label}
          {currentState.enabled ? '' : ' · currently hidden'}
        </Typography>
      </Box>

      {/* ── workspace ── */}
      <Box sx={{
        display: 'grid',
        // The house ladder, 900 and 640, as the editor and the Cork Board use
        // it — not MUI's sm/lg, which had this page rearranging at 600 and
        // 1200 while the other two moved at 640 and 900.
        //
        // Default is the tablet band: list beside the work surface, preview
        // floating. Above 901 the preview docks as a third column; at 640 and
        // below the list stops being a column at all (see below).
        gridTemplateColumns: '220px 1fr',
        [BP.phone]: { gridTemplateColumns: '1fr' },
        [BP.docked]: { gridTemplateColumns: '250px 1fr 1.15fr' },
        minHeight: '70vh',
        // The anchor for the floating preview, and the clip that keeps the
        // parked panel from widening the document. Without the clip the page
        // scrolls sideways to reveal a panel that is meant to be hidden.
        position: 'relative',
        overflowX: 'hidden',
      }}>

        {/* element list */}
        <Box sx={{
          backgroundColor: SP.purpleDeep,
          py: 2.25,
          overflowY: 'auto',
          // On a phone a full-height list of twenty-six would put the template
          // fields a screen and a half down, and tapping a row would look like
          // nothing happening. It becomes a single scrolling row instead, so
          // the thing you came to edit is the thing on screen.
          [BP.phone]: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            overflowX: 'auto',
            overflowY: 'hidden',
            py: 0,
          },
        }}>
          <Typography sx={{
            fontFamily: FONT.mono,
            fontSize: 9.5,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)',
            px: 2.5,
            py: 0.75,
            // The category tabs directly above already say Front Matter; in a
            // horizontal strip this repeats it and costs a row of width.
            [BP.phone]: { display: 'none' },
          }}>
            {/* Read off LOCATIONS rather than spelled out again: this heading and
                the mode tab above it name the same thing, and when they were two
                separate strings the tab could be renamed without it. */}
            {activeMode === 'editor'
              ? CATEGORIES[activeCategory].label
              : `${LOCATIONS.cover.label} elements`}
          </Typography>

          {/* Elements start off, so a book nobody has chosen sections for reads as
              broken unless the page says what to do. Shown once, until the author
              switches something on. */}
          {noneEnabled && (
            <Typography sx={{
              mx: 2.5,
              mb: 1.5,
              px: 1.5,
              py: 1.25,
              borderRadius: '4px',
              border: `1px dashed ${SP.lineLt}`,
              backgroundColor: 'rgba(255,255,255,0.04)',
              fontFamily: FONT.sans,
              fontSize: 11.5,
              lineHeight: 1.6,
              color: 'rgba(255,255,255,0.7)',
              // A paragraph inside a horizontal strip would be a single very
              // wide cell pushing every element off the screen.
              [BP.phone]: { display: 'none' },
            }}>
              {coverOnly
                ? 'Nothing is switched on yet. Switch on the parts this cover has.'
                : 'Nothing is in your book yet. Switch on the sections it has — they are printed in your exported PDF, in book order.'}
            </Typography>
          )}

          {listedElements.map((el) => {
            const state = structure[el.id] || defaultElementState(el);
            const isCurrent = el.id === currentId;
            return (
              <Box
                key={el.id}
                component="button"
                type="button"
                onClick={() => goToElement(el.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  width: '100%',
                  textAlign: 'left',
                  px: 2.5,
                  py: 1.15,
                  border: 'none',
                  borderLeft: `3px solid ${isCurrent ? SP.gold : 'transparent'}`,
                  // In the strip the rows sit side by side and keep their own
                  // width, and the current marker moves to the underside —
                  // a left border on a horizontal row marks nothing.
                  [BP.phone]: {
                    width: 'auto',
                    flexShrink: 0,
                    borderLeft: 'none',
                    borderBottom: `3px solid ${isCurrent ? SP.gold : 'transparent'}`,
                    whiteSpace: 'nowrap',
                  },
                  cursor: 'pointer',
                  transition: 'all .15s ease',
                  fontFamily: FONT.sans,
                  fontSize: 13,
                  opacity: state.enabled ? 1 : 0.42,
                  color: isCurrent ? SP.white : 'rgba(255,255,255,0.72)',
                  fontWeight: isCurrent ? 500 : 400,
                  backgroundColor: isCurrent ? 'rgba(212,175,55,0.14)' : 'transparent',
                  '&:hover': {
                    backgroundColor: isCurrent ? 'rgba(212,175,55,0.14)' : 'rgba(255,255,255,0.05)',
                    color: SP.white,
                  },
                }}
              >
                <Box component="span" sx={{
                  fontFamily: FONT.mono,
                  fontSize: 10,
                  flexShrink: 0,
                  color: isCurrent ? SP.goldLight : 'rgba(255,255,255,0.35)',
                }}>
                  {String(el.order).padStart(2, '0')}
                </Box>
                <Box component="span" sx={{
                  flex: 1,
                  textDecoration: state.enabled ? 'none' : 'line-through',
                  textDecorationColor: 'rgba(255,255,255,0.3)',
                }}>
                  {el.name}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* template editor — lands in the next phase */}
        <Box sx={{
          px: 3,
          py: 3,
          backgroundColor: SP.white,
          borderRight: { lg: `1px solid ${SP.lineSoft}` },
          overflowY: 'auto',
        }}>
          <Typography sx={{ fontFamily: FONT.serif, fontSize: 21, fontWeight: 600 }}>
            {current.name}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
            {current.roles.map((role) => (
              <Box key={role} sx={{
                fontFamily: FONT.mono,
                fontSize: 9.5,
                backgroundColor: SP.lavender,
                color: SP.purple,
                px: 1,
                py: 0.4,
                borderRadius: '999px',
              }}>
                {role}
              </Box>
            ))}
          </Box>
          <Typography sx={{ color: SP.gray, fontSize: 13, lineHeight: 1.65, mt: 1.5 }}>
            {current.purpose}
          </Typography>

          {/* The only include/exclude control on the page. It used to be
              duplicated per row in the dark column; the rows now select and
              this switches. Framed so it reads as the control rather than a
              status line, since it is no longer next to the thing it acts on. */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mt: 2.25,
            px: 1.75,
            py: 1.5,
            borderRadius: '10px',
            border: `1px solid ${currentState.enabled ? SP.gold : SP.lineSoft}`,
            backgroundColor: currentState.enabled ? 'rgba(212,175,55,0.08)' : 'transparent',
            transition: 'background-color .2s ease, border-color .2s ease',
          }}>
            <ElementToggle
              on={currentState.enabled}
              label={`Include ${current.name} in the book`}
              onChange={(next) => updateElement(current.id, { enabled: next })}
            />
            <Typography sx={{
              fontFamily: FONT.sans,
              fontSize: 13.5,
              fontWeight: 500,
              color: currentState.enabled ? SP.purpleDeep : SP.gray,
            }}>
              {currentState.enabled ? 'Included in the book' : 'Hidden from the book'}
            </Typography>
          </Box>

          <Box sx={{ mt: 2.25 }}>
            <Typography sx={{ ...eyebrowSx, display: 'block', mb: 0.75 }}>
              Position on page
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {POSITIONS.map((pos) => {
                const active = currentState.position === pos;
                return (
                  <Box
                    key={pos}
                    component="button"
                    type="button"
                    aria-pressed={active}
                    onClick={() => updateElement(current.id, { position: pos })}
                    sx={{
                      flex: 1,
                      px: 0.75,
                      py: 1,
                      fontFamily: FONT.mono,
                      fontSize: 10.5,
                      letterSpacing: '.03em',
                      textTransform: 'uppercase',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all .18s ease',
                      border: `1px solid ${active ? SP.purple : SP.lineSoft}`,
                      backgroundColor: active ? SP.purple : SP.white,
                      color: active ? SP.white : SP.gray,
                      fontWeight: active ? 600 : 400,
                      '&:hover': { borderColor: active ? SP.purple : SP.purpleSoft },
                    }}
                  >
                    {pos}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <StructureFormatToolbar
            format={currentState.format}
            onChange={(format) => updateElement(current.id, { format })}
          />

          <TemplateFields
            element={current}
            fields={currentState.fields}
            onChangeField={(key, value) => updateField(current.id, key, value)}
            contents={tocContents}
            editorPath={coverOnly ? null : `/editor/${id}`}
          />

          <Box sx={{ mt: 3.25 }}>
            <Box
              component="button"
              type="button"
              onClick={() => setResetOpen(true)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                px: 1.75,
                py: 1,
                fontFamily: FONT.mono,
                fontSize: 11,
                color: SP.gray,
                border: `1px solid ${SP.lineSoft}`,
                borderRadius: '3px',
                backgroundColor: SP.white,
                cursor: 'pointer',
                transition: 'all .2s ease',
                '&:hover': { borderColor: SP.black, color: SP.black },
              }}
            >
              <RestartAltIcon sx={{ fontSize: 15 }} />
              Reset this template
            </Box>
          </Box>
        </Box>

        {/* The handle that summons the preview, at 900px and below only. Above
            that the preview is the third column and a control to reveal it
            would have no job — the same reasoning as `.rail-handle` in
            EditorPage.css and the Cork Board's details handle. */}
        <Box
          component="button"
          type="button"
          aria-label={previewOpen ? 'Hide preview' : 'Show preview'}
          aria-expanded={previewOpen}
          onClick={() => setPreviewOpen((v) => !v)}
          sx={{
            display: 'none',
            [BP.panelFloats]: { display: 'flex' },
            position: 'absolute',
            top: '50%',
            // Rides out with the panel, off the same token, so the two cannot
            // drift apart.
            right: previewOpen ? FLOATING_PANEL_W : 0,
            zIndex: 31,
            transform: 'translateY(-50%)',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 56,
            p: 0,
            cursor: 'pointer',
            border: `1px solid ${SP.lineSoft}`,
            borderRight: 'none',
            borderRadius: '4px 0 0 4px',
            backgroundColor: SP.white,
            color: SP.purple,
            boxShadow: '-4px 0 12px rgba(20,10,40,0.18)',
            transition: 'right .18s ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          {previewOpen
            ? <ChevronRightIcon sx={{ fontSize: 16 }} />
            : <ChevronLeftIcon sx={{ fontSize: 16 }} />}
        </Box>

        {/* preview */}
        <Box sx={{
          px: 3.25,
          py: 3,
          backgroundColor: SP.purpleDeep,
          // Below 901 it floats over the page rather than stacking under it.
          // It used to switch off entirely under 1200, which took the preview
          // away from an iPad in landscape — 800px of work surface and no
          // picture of the page it was making.
          [BP.panelFloats]: {
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            zIndex: 30,
            width: FLOATING_PANEL_W,
            overflowY: 'auto',
            boxShadow: '-8px 0 24px rgba(20,10,40,0.35)',
            transform: previewOpen ? 'none' : 'translateX(100%)',
            transition: 'transform .18s ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          },
        }}>
          <StructurePreview
            element={current}
            state={previewState}
            onPrev={() => stepPage(-1)}
            onNext={() => stepPage(1)}
            navDisabled={enabledInMode.length < 2}
            trimSize={draft?.trimSize || DEFAULT_BOOK_SIZE}
          />
        </Box>
      </Box>

      <ConfirmModal
        open={resetOpen}
        title="Reset this template?"
        highlight={current.name}
        message="Its text, position and formatting go back to the defaults. Whether it is included in the book is left as it is."
        confirmLabel="Reset"
        onConfirm={() => { resetElement(current); setResetOpen(false); }}
        onCancel={() => setResetOpen(false)}
      />
    </Box>
  );
}

// --- small pieces ---------------------------------------------------------

/** A link out to one of the two tools, or an inert version of it when disabled. */
function ToolLink({ to, dotColor, disabled, disabledTitle, children }) {
  const sx = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.9,
    fontFamily: FONT.mono,
    fontSize: 10.5,
    px: 1.6,
    py: 0.9,
    borderRadius: '3px',
    border: `1px solid ${SP.lineLt}`,
    textDecoration: 'none',
    transition: 'all .2s ease',
    color: disabled ? 'rgba(255,255,255,0.35)' : SP.white,
    cursor: disabled ? 'not-allowed' : 'pointer',
    ...(disabled ? {} : { '&:hover': { borderColor: SP.gold, color: SP.goldLight } }),
  };

  const dot = (
    <Box sx={{
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: disabled ? 'rgba(255,255,255,0.25)' : dotColor,
    }} />
  );

  // Rendered as a span rather than a disabled link: an <a> with no href is still
  // in the tab order in some browsers, and "not clickable" has to mean it.
  if (disabled) {
    return (
      <Box component="span" title={disabledTitle} aria-disabled="true" sx={sx}>
        {dot}{children}
      </Box>
    );
  }

  return (
    <Box component={RouterLink} to={to} sx={sx}>
      {dot}{children}
    </Box>
  );
}

/** Whether the last change reached the server. */
function SaveIndicator({ state }) {
  const text = {
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Not saved',
  }[state];
  if (!text) return null;

  return (
    <Typography sx={{
      fontFamily: FONT.mono,
      fontSize: 10.5,
      mr: 0.5,
      color: state === 'error' ? '#F0A0A0' : 'rgba(255,255,255,0.6)',
    }}>
      {text}
    </Typography>
  );
}
